/**
 * First-Time Login Page
 * Self-service magic link generation for users who need to activate their accounts
 */

import React, { useState } from 'react';
import { Loader2, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export const FirstTimeLoginPage: React.FC = () => {
  // Redirect to main login page which has proper signup flow
  React.useEffect(() => {
    window.location.href = '/login';
  }, []);

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    setStatus('sending');
    setMessage('');

    try {
      // Use Supabase's OTP (magic link) method
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        // Check if it's a rate limit error
        if (error.message.includes('rate limit')) {
          setStatus('error');
          setMessage('Too many requests. Please try again in a few minutes.');
        } else {
          setStatus('error');
          setMessage(error.message);
        }
      } else {
        setStatus('success');
        setMessage('Magic link sent! Check your email and click the link to activate your account.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('An unexpected error occurred. Please try again.');
      console.error('Magic link error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">First-Time Login</h1>
          <p className="text-purple-200">
            Welcome to Hover! Request a magic link to activate your account.
          </p>
        </div>

        {status === 'success' ? (
          <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Check Your Email!</h2>
            <p className="text-green-200 mb-4">{message}</p>
            <p className="text-sm text-purple-300">
              The magic link will expire in 1 hour. After clicking it, you'll be able to:
            </p>
            <ul className="text-sm text-purple-300 mt-2 space-y-1">
              <li>✓ Access your account</li>
              <li>✓ Set a password</li>
              <li>✓ Use password reset in the future</li>
            </ul>
          </div>
        ) : (
          <form onSubmit={handleSendMagicLink} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-purple-200 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="w-full px-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={status === 'sending'}
                required
              />
            </div>

            {status === 'error' && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-200">{message}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {status === 'sending' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending Magic Link...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Send Magic Link
                </>
              )}
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-purple-500/30">
          <p className="text-sm text-purple-300 text-center">
            Already activated your account?{' '}
            <a href="/" className="text-purple-400 hover:text-purple-300 underline">
              Sign in here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
